import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import type { SkinnyBannerType } from '@/types/zones/slices/skinny-banner-slice';
import { getEventPropsBuilder } from '../../utils';

export type SkinnyBannerAction = 'viewed' | 'clicked';

type SkinnyBannerEventInput = {
    bannerType: SkinnyBannerType;
    action: SkinnyBannerAction;
    ctaUrl?: string;
};

const EVENT_NAMES: Record<SkinnyBannerType, Record<SkinnyBannerAction, AmplitudeEventKeys['event']>> = {
    game: { viewed: 'Game Banner Viewed', clicked: 'Game Banner Clicked' },
    promo_details: { viewed: 'Promo Banner Viewed', clicked: 'Promo Banner Clicked' },
    category: { viewed: 'Category Banner Viewed', clicked: 'Category Banner Clicked' },
    refer: { viewed: 'RAF Banner Viewed', clicked: 'RAF Banner Clicked' }
};

const parseUrl = (url?: string): URL | null => {
    if (!url) return null;
    try {
        return new URL(url);
    } catch {
        return null;
    }
};

export const extractGameId = (url?: string): string => {
    const parsed = parseUrl(url);
    if (!parsed) return '';
    const segments = parsed.pathname.split('/').filter(Boolean);
    return segments.length === 1 ? segments[0] : '';
};

export const extractPromoName = (url?: string): string => {
    const parsed = parseUrl(url);
    if (!parsed) return '';
    const match = parsed.pathname.match(/\/rewards\/([^/?#]+)/);
    return match?.[1] ?? '';
};

export const extractCategory = (url?: string): string => {
    const parsed = parseUrl(url);
    if (!parsed) return '';
    const match = parsed.pathname.match(/\/c\/([^/?#]+)/);
    return match?.[1] ?? '';
};

const BASE_PROPS: Partial<AmplitudeEventKeys> = {
    Module: 'lobby',
    'Banner Format': 'skinny'
};

export const getSkinnyBannerEventProps = getEventPropsBuilder(
    ({ bannerType, action, ctaUrl }: SkinnyBannerEventInput): Partial<AmplitudeEventKeys> | null => {
        const eventName = EVENT_NAMES[bannerType]?.[action];
        if (!eventName) return null;

        const props: Partial<AmplitudeEventKeys> = { event: eventName, ...BASE_PROPS };

        switch (bannerType) {
            case 'game':
                props['Game ID'] = extractGameId(ctaUrl);
                return props;
            case 'promo_details':
                props['Promo Name'] = extractPromoName(ctaUrl);
                return props;
            case 'category':
                props.Category = extractCategory(ctaUrl);
                return props;
            case 'refer':
            default:
                return props;
        }
    }
);
